import { Bar, BarConfig } from '@ant-design/plots';
import { useLazyQuery } from '@apollo/client';
import {
  GET_IDEA_COUNT_BY_CHANNEL_TAGS,
  IdeaCountByTags,
} from '@frontend/data-access';
import { Card } from 'antd';
import { useEffect, useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';

function IdeaCountsByTags({
  channelId,
  className,
  challengeId,
  innovationSpaceId,
}: {
  channelId?: string;
  className?: string;
  challengeId?: string;
  innovationSpaceId?: string;
}) {
  const { t } = useTranslation('common');
  const [getIdeaCountByTags, { data, loading }] = useLazyQuery<{
    ideaCountByChannelTags: IdeaCountByTags[];
  }>(GET_IDEA_COUNT_BY_CHANNEL_TAGS);

  useEffect(() => {
    if (!channelId) return;
    getIdeaCountByTags({
      variables: { channelId, challengeId, spaceId: innovationSpaceId },
    });
  }, [channelId, challengeId, innovationSpaceId, getIdeaCountByTags]);

  const chartData = useMemo(() => {
    const realData = data?.ideaCountByChannelTags || [];
    if (realData.length > 0) {
      return realData;
    }
    // Return placeholder data with 0 count when no data is available
    return [{ channelTag: t('no_data'), count: 0 }];
  }, [data, t]);

  const config: BarConfig = {
    data: chartData,
    loading,
    xField: 'channelTag',
    yField: 'count',
    legend: false,
    scale: {
      color: {
        range: ['#529FF0'],
      },
    },
    marginRight: 20,
    label: {
      text: 'count',
      position: 'right',
      textAlign: 'start',
      dx: 8,
    },
    // Ensure y-axis starts from 0 and includes at least value 1
    yAxis: {
      min: 0,
      max: chartData.some((item) => item.count > 0) ? undefined : 1,
    },
  };

  return (
    <div className={className}>
      <Card style={{ borderRadius: 20 }}>
        <div className="space-y-4">
          <div className="text-sm text-gray-900 font-medium">
            {t('ideas_counts_by_tags')}
          </div>
          <Bar {...config} />
        </div>
      </Card>
    </div>
  );
}

export default IdeaCountsByTags;
